/**
 * Source Index - Consolidated source attribution for the dossier.
 *
 * Lists all sources accessed with dates and types.
 */

import { ExecutiveSourceType, BioFragment } from './types';

interface SourceIndexProps {
  sourceTypes?: ExecutiveSourceType[];
  verificationSources?: string[];
  bioFragments?: BioFragment[];
  lastVerified?: string;
}

// Source type metadata
const sourceTypeLabels: Record<ExecutiveSourceType, { label: string; tier: string; color: string }> = {
  company_website: { label: 'Company Website', tier: '[TIER_1]', color: 'text-green-600' },
  sec_filing: { label: 'SEC Filing', tier: '[GOV]', color: 'text-green-600' },
  linkedin: { label: 'LinkedIn', tier: '[SOCIAL]', color: 'text-blue-600' },
  news_article: { label: 'News Article', tier: '[TIER_2]', color: 'text-yellow-600' },
  press_release: { label: 'Press Release', tier: '[TIER_1]', color: 'text-primary-600' },
  brave_search: { label: 'Web Search', tier: '[TIER_3]', color: 'text-gray-600' },
  manual: { label: 'Manual Entry', tier: '[UNVERIFIED]', color: 'text-purple-600' },
};

interface SourceEntry {
  name: string;
  url?: string;
  tier: string;
  date?: string;
  isAuthoritative: boolean;
}

export function SourceIndex({
  sourceTypes = [],
  verificationSources = [],
  bioFragments = [],
  lastVerified,
}: SourceIndexProps) {
  // Consolidate all sources
  const sources: SourceEntry[] = [];

  // Add source types as entries
  sourceTypes.forEach(st => {
    const meta = sourceTypeLabels[st];
    if (meta) {
      sources.push({
        name: meta.label,
        tier: meta.tier,
        isAuthoritative: st === 'sec_filing' || st === 'company_website',
      });
    }
  });

  // Add verification sources
  verificationSources.forEach(url => {
    const domain = new URL(url).hostname.replace('www.', '');
    sources.push({
      name: domain,
      url,
      tier: '[VERIFICATION]',
      isAuthoritative: url.includes('sec.gov') || url.includes('linkedin.com'),
    });
  });

  // Add bio fragment sources with dates
  bioFragments.forEach(fragment => {
    if (fragment.source_url) {
      const domain = new URL(fragment.source_url).hostname.replace('www.', '');
      sources.push({
        name: `${sourceTypeLabels[fragment.source_type as ExecutiveSourceType]?.label || fragment.source_type} - ${domain}`,
        url: fragment.source_url,
        tier: fragment.is_authoritative ? '[AUTHORITATIVE]' : '[TIER_2]',
        date: fragment.extracted_at,
        isAuthoritative: fragment.is_authoritative,
      });
    }
  });

  // Deduplicate by URL or name
  const uniqueSources = sources.reduce((acc, source) => {
    const key = source.url || source.name;
    if (!acc.has(key)) {
      acc.set(key, source);
    }
    return acc;
  }, new Map<string, SourceEntry>());

  const sourceList = Array.from(uniqueSources.values());

  if (sourceList.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-gray-500">
        No source information available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900">Source Index</h3>
        {lastVerified && (
          <span className="text-xs text-gray-500">
            Last verified: {new Date(lastVerified).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="divide-y divide-gray-100">
        {sourceList.map((source, idx) => (
          <div key={idx} className="py-2 flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-mono ${source.isAuthoritative ? 'text-green-600' : 'text-gray-500'}`}>
                  {source.tier}
                </span>
                {source.url ? (
                  <a
                    href={source.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-500 truncate"
                  >
                    {source.name}
                  </a>
                ) : (
                  <span className="text-sm text-gray-700">{source.name}</span>
                )}
                {source.isAuthoritative && (
                  <svg className="h-4 w-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </div>
            {source.date && (
              <span className="text-xs text-gray-400 flex-shrink-0">
                {new Date(source.date).toLocaleDateString()}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="pt-4 border-t border-gray-100">
        <p className="text-xs text-gray-500 mb-2">Source Tiers:</p>
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="text-green-600">[GOV] Government/Regulatory</span>
          <span className="text-green-600">[TIER_1] Authoritative</span>
          <span className="text-yellow-600">[TIER_2] High Credibility</span>
          <span className="text-gray-600">[TIER_3] Estimates</span>
          <span className="text-blue-600">[SOCIAL] Social Platforms</span>
        </div>
      </div>
    </div>
  );
}

export default SourceIndex;
